import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Download, 
  Calendar, 
  Clock, 
  Database, 
  Loader2, 
  FileArchive,
  History,
  Settings,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));

const HOURS = Array.from({ length: 24 }, (_, i) => { 
  const h = i % 12 || 12;
  const ampm = i < 12 ? 'AM' : 'PM';
  return { value: i, label: `${h}:00 ${ampm}` };
});

export default function BackupManagementPage() {
  const { getAuthHeader, employee } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('All'); // 'Admin' | 'IT' | 'All'

  // Sync to global header (skip 'All' so it doesn't fight other pages)
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { if (moduleFilter === 'Admin' || moduleFilter === 'IT') setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 6,
    day_of_month: 1,
    time_hour: 0,
    time_minute: 0,
    timezone: 'Asia/Kolkata'
  });

  const isAuthorized = employee?.role === 'Admin Head' || employee?.role === 'Super Admin';

  const fetchBackupHistory = useCallback(async () => {
    if (!isAuthorized) return;
    
    try {
      const res = await fetch(`${API}/backup/history?module=${moduleFilter}`, {
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        setBackupHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch backup history:', error);
    }
  }, [getAuthHeader, isAuthorized, moduleFilter]);

  const fetchSchedule = useCallback(async () => {
    if (!isAuthorized) return;
    
    try {
      const res = await fetch(`${API}/backup/schedule?module=${moduleFilter}`, {
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  }, [getAuthHeader, isAuthorized, moduleFilter]);

  useEffect(() => {
    if (isAuthorized) {
      setLoading(true);
      Promise.all([fetchBackupHistory(), fetchSchedule()]).finally(() => setLoading(false));
    }
  }, [fetchBackupHistory, fetchSchedule, isAuthorized]);

  const handleGenerateBackup = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/backup/download?module=${moduleFilter}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const modTag = moduleFilter === 'All' ? '' : `${moduleFilter}_`;
        a.download = `AdminDesk_${modTag}Backup_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`${moduleFilter} backup generated, stored in cloud, and downloaded!`);
        fetchBackupHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate backup');
      }
    } catch (error) {
      toast.error('Failed to generate backup');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadFromCloud = async (backupId, filename) => {
    try {
      const res = await fetch(`${API}/backup/download/${backupId}`, {
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Backup downloaded!');
      } else {
        toast.error('Failed to download backup from cloud');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await fetch(`${API}/backup/schedule?module=${moduleFilter}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(schedule)
      });
      
      if (res.ok) {
        toast.success(`${moduleFilter} schedule saved successfully!`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save schedule');
      }
    } catch (error) {
      toast.error('Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthorized) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <CardTitle className="text-destructive">Access Denied</CardTitle>
              <CardDescription>
                You don't have permission to access Backup Management.
                <br />
                This feature is only available to authorized Admin Heads.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Backup Management</h1>
            <p className="text-muted-foreground">
              Generate, schedule, and review backups — segregated by department
            </p>
          </div>

          {/* Department slicer — Admin / IT / All */}
          <div
            className="flex items-center gap-1 border rounded-md p-1 bg-card shrink-0"
            data-testid="backup-module-slicer"
            role="tablist"
            aria-label="Backup department slicer"
          >
            {[
              { v: 'Admin', l: 'Admin' },
              { v: 'IT', l: 'IT' },
              { v: 'All', l: 'All' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setModuleFilter(opt.v)}
                data-testid={`backup-module-${opt.v.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs rounded font-medium transition ${
                  moduleFilter === opt.v
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* Active scope hint */}
        <div className="text-xs text-muted-foreground bg-muted/40 border rounded-md px-3 py-2" data-testid="backup-scope-hint">
          <strong>Active scope:</strong>{' '}
          {moduleFilter === 'All'
            ? 'Full system — every collection, both Admin & IT data.'
            : `${moduleFilter} only — ${moduleFilter} tickets, ${moduleFilter} daily expenses, ${moduleFilter} expense masters${moduleFilter === 'IT' ? ' & IT Request Types' : ' & Admin Request Types'}. Employees / departments / users are shared and always included.`}
        </div>

        <Tabs defaultValue="manual" className="space-y-4">
          <TabsList>
            <TabsTrigger value="manual" data-testid="tab-manual">
              <Download className="w-4 h-4 mr-2" />
              Manual Backup
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled">
              <Calendar className="w-4 h-4 mr-2" />
              Scheduled Backup
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              Backup History
            </TabsTrigger>
          </TabsList>

          {/* Manual Backup Tab */}
          <TabsContent value="manual">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Generate Manual {moduleFilter === 'All' ? '' : `${moduleFilter} `}Backup
                </CardTitle>
                <CardDescription>
                  {moduleFilter === 'All'
                    ? 'Create a full system backup including all data (Admin + IT).'
                    : `Create a ${moduleFilter} department backup — only ${moduleFilter}-scoped data is exported.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium">This {moduleFilter === 'All' ? 'Full' : moduleFilter} Backup Includes:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {moduleFilter === 'All' ? (
                      <>
                        <li>• All Tickets & Request Data (both Admin & IT)</li>
                        <li>• Admin + IT Request Types</li>
                        <li>• Daily Expenses (Admin + IT) + Expense Masters</li>
                        <li>• Employee Master Data, Departments & Users</li>
                        <li>• Notifications, Ticket Chats, File Metadata, Backup History</li>
                      </>
                    ) : moduleFilter === 'IT' ? (
                      <>
                        <li>• IT Tickets only (module = IT)</li>
                        <li>• IT Request Types (taxonomy)</li>
                        <li>• IT Daily Expenses + IT Expense Categories/Subcategories/Vendors</li>
                        <li>• Shared: Employees, Departments, Users, File metadata</li>
                      </>
                    ) : (
                      <>
                        <li>• Admin Tickets only (module = Admin)</li>
                        <li>• Admin Custom + Default Request Types</li>
                        <li>• Admin Daily Expenses + Admin Expense Categories/Subcategories/Vendors</li>
                        <li>• Shared: Employees, Departments, Users, File metadata</li>
                      </>
                    )}
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">
                    Backups are stored in cloud storage and persist across deployments.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button 
                    onClick={handleGenerateBackup}
                    disabled={generating}
                    className="min-w-[200px]"
                    data-testid="generate-backup-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileArchive className="w-4 h-4 mr-2" />
                        Generate {moduleFilter === 'All' ? 'Full' : moduleFilter} Backup
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Backup will be downloaded as Excel and stored in cloud
                  </span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduled Backup Tab */}
          <TabsContent value="scheduled">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {moduleFilter === 'All' ? 'Full' : moduleFilter} Scheduled Backup Configuration
                </CardTitle>
                <CardDescription>
                  Configure automatic backup schedule for the <strong>{moduleFilter}</strong> scope. Each department maintains its own schedule.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Scheduled Backup</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically generate backups based on schedule
                    </p>
                  </div>
                  <Switch
                    checked={schedule.enabled}
                    onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, enabled: checked }))}
                    data-testid="schedule-toggle"
                  />
                </div>

                {/* Frequency Selection */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={schedule.frequency}
                      onValueChange={(value) => setSchedule(prev => ({ ...prev, frequency: value }))}
                      disabled={!schedule.enabled}
                    >
                      <SelectTrigger className="w-[200px]" data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Day Selection */}
                  {schedule.frequency === 'weekly' ? (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select
                        value={schedule.day_of_week.toString()}
                        onValueChange={(value) => setSchedule(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                        disabled={!schedule.enabled}
                      >
                        <SelectTrigger className="w-[200px]" data-testid="day-of-week-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day) => (
                            <SelectItem key={day.value} value={day.value.toString()}>
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Select
                        value={schedule.day_of_month.toString()}
                        onValueChange={(value) => setSchedule(prev => ({ ...prev, day_of_month: parseInt(value) }))}
                        disabled={!schedule.enabled}
                      >
                        <SelectTrigger className="w-[200px]" data-testid="day-of-month-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_MONTH.map((day) => (
                            <SelectItem key={day.value} value={day.value.toString()}>
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Select
                      value={schedule.time_hour.toString()}
                      onValueChange={(value) => setSchedule(prev => ({ ...prev, time_hour: parseInt(value) }))}
                      disabled={!schedule.enabled}
                    >
                      <SelectTrigger className="w-[200px]" data-testid="time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map((hour) => (
                          <SelectItem key={hour.value} value={hour.value.toString()}>
                            {hour.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Timezone */}
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded w-fit">
                      <Clock className="w-4 h-4 inline mr-2" />
                      {schedule.timezone}
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveSchedule}
                  disabled={savingSchedule}
                  data-testid="save-schedule-btn"
                >
                  {savingSchedule ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Schedule'
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Backup History
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchBackupHistory}
                    data-testid="refresh-history-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription>
                  View all generated backups
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : backupHistory.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileArchive className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No backup history found</p>
                    <p className="text-sm">Generate your first backup to see it here</p>
                  </div>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Backup Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>File Name</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Records</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backupHistory.map((backup) => (
                        <TableRow key={backup.backup_id}>
                          <TableCell>{formatDate(backup.created_at)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              backup.backup_type === 'manual' 
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                                : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                            }`}>
                              {backup.backup_type === 'manual' ? 'Manual' : 'Scheduled'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              backup.module === 'IT'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : backup.module === 'Admin'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                  : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                            }`} data-testid={`backup-module-cell-${backup.backup_id}`}>
                              {backup.module || 'All'}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-xs max-w-[260px] truncate" title={backup.filename}>{backup.filename}</TableCell>
                          <TableCell>{formatFileSize(backup.file_size)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {backup.stats?.tickets || 0} tickets, {backup.stats?.employees || 0} employees
                          </TableCell>
                          <TableCell>
                            {backup.storage_path ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadFromCloud(backup.backup_id, backup.filename)}
                                data-testid={`download-${backup.backup_id}`}
                              >
                                <Download className="w-3.5 h-3.5 mr-1" />
                                Download
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Local only</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
